import { Component, inject, Renderer2 } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subject, first, forkJoin, takeUntil, switchMap, of, tap, map, catchError, EMPTY } from 'rxjs';
import { environment } from '../environments/environment';
import { ALERT_ERROR_NAME } from './common/alert/alert-error/alert-error-name';
import { ALERT_FLASH_NAME } from './common/alert/alert-flash/alert-flash-name';
import { AuthService } from './common/core/auth/auth.service';
import { LAST_USED_TENANT_ID, LAST_USED_TENANT_NAME } from './common/core/auth/custom';
import { Profile } from './common/core/auth/profile';
import { PROFILE_ROLE } from './common/core/auth/profile-role';
import { ProfileService } from './common/core/auth/profile.service';
import { ConfigService } from './common/core/config/config.service';
import { DomainService } from './common/core/domain/domain.service';
import { HOSTNAME } from './common/core/domain/hostname';
import { WindowRef } from './common/core/domain/window-ref';
import { I18NService } from './common/core/i18n/i18n.service';
import { LoadingWidgetComponent } from './common/core/loading-widget/loading-widget.component';
import { NavShellComponent } from './common/navbar/nav-shell.component';
import { NOTIFICATION_COPY_USER_ACCESS, NOTIFICATION_EXPORT_KEYS, NOTIFICATION_INCLUDE_ALL_MEMBERS, NotificationItem } from './notification-item';
import { NotificationsHub } from './notifications-hub';
import { AlertErrorComponent } from './common/alert/alert-error/alert-error.component';
import { AlertFlashComponent } from './common/alert/alert-flash/alert-flash.component';
import { TOKEN_RESPONSE } from './common/core/auth/connect-storage-keys';
import { Context } from './common/core/auth/context';
import { ContextResponse } from './common/core/auth/context-response';
import { ADMIN_UI_CURRENT_CONTEXT, ADMIN_UI_TOKEN_KIND, TOKEN_KIND_KALMAR } from './common/core/auth/storage-keys';
import { PATH } from './common/core/const/path';
import { EntityOptionsMenuDataService } from './common/entity-options-menu/entity-options-menu-data.service';
import { ServiceAccessDataService } from './common/service-access/service-access-data.service';
import { DataDogService } from './data-dog.service';
import { WelcomeDialogComponent } from './welcome-dialog/welcome-dialog.component';
import { WelcomeDialogDataService } from './welcome-dialog/welcome-dialog-data.service';
import { AlertFlashDataService } from './common/alert/alert-flash/alert-flash-data.service';
import { AlertErrorDataService } from './common/alert/alert-error/alert-error-data.service';
import { EntityDetailsDataService } from './common/entity-details/entity-details-data.service';
import { ENTITY_TYPE } from './common/core/const/entity-type';
import { THEME } from './common/core/auth/theme';
import { LatestExportsService } from './common/exports/latest-exports.service';


@Component({
  selector: 'adminui-layout',
  imports: [RouterModule, AlertFlashComponent, AlertErrorComponent, LoadingWidgetComponent, WelcomeDialogComponent, NavShellComponent],
  templateUrl: './layout.component.html'
})
export class LayoutComponent {

  alertFlashName = ALERT_FLASH_NAME;
  alertErrorName = ALERT_ERROR_NAME;
  
  hasProfile = false;
  showWelcomeDialog = false;
  notificationTitle: string = '';
  profileRole = PROFILE_ROLE;

  notificationItem!: NotificationItem | null;
  showNotification = false;

  message: { [key: string]: string } = {};

  private notificationTimeout: any;
  private webSocket!: WebSocket;
  private pingInterval!: any;
  private unlistenVisibility!: () => void;
  private checkSessionIframe: HTMLIFrameElement | null = null;
  private checkSessionInterval: any = null;
  private checkSessionEstablished = false;
  private unlistenCheckSessionMessage!: () => void;

  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  private renderer2 = inject(Renderer2);
  private windowRef = inject(WindowRef);
  private configService = inject(ConfigService);
  private i18nService = inject(I18NService);
  private domainService = inject(DomainService);
  private router = inject(Router);
  private entityOptionsMenuDataService = inject(EntityOptionsMenuDataService);
  private serviceAccessDataService = inject(ServiceAccessDataService);
  private dataDogService = inject(DataDogService);
  private welcomeDialogDataService = inject(WelcomeDialogDataService);
  private alertFlashDataService = inject(AlertFlashDataService);
  private alertErrorDataService = inject(AlertErrorDataService);
  private entityDetailsDataService = inject(EntityDetailsDataService);
  private latestExportsService = inject(LatestExportsService);

  private destroy$ = new Subject<void>();

  get isCustomerOnboarding(): boolean {
    return this.router.url.includes(PATH.customer_onboarding);
  }

  ngOnInit(): void {
    this.unlistenVisibility = this.renderer2.listen('document', 'visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (this.authService.isAccessTokenExpired()) {
          this.authService.handleUnauthorized();
        }
      }
    });

    const hostname = this.windowRef.getHostname();
    this.initCheckSession(hostname);

    this.authService.getUserProfile().pipe(first()).subscribe(profile => {
      // A Kalmar (Administrator) session has no tenant-enabled token, so the context can't be resolved
      // against the AMA claims; it is persisted in session storage on login / context switch instead.
      const isKalmarSession = sessionStorage.getItem(ADMIN_UI_TOKEN_KIND) === TOKEN_KIND_KALMAR;

      // The Kalmar token may still carry a default tenant_id (from the tenant-bound AMA read scopes),
      // but it must be ignored for an Administrator: only tenant sessions reconcile the token tenant.
      if (!isKalmarSession) {
        this.handleLastUsedContextIdDifferentThanTenantIdFromToken(profile, hostname);
      }

      this.profileService.profile = profile;
      this.handleI18N();

      const storedContext = this.getStoredContext();
      const contextResponse$ = isKalmarSession
        ? of({ contexts: storedContext ? [storedContext] : [] } as ContextResponse)
        : this.authService.getContextResponseById(profile.custom[LAST_USED_TENANT_ID]);

      forkJoin({
        contextResponse: contextResponse$,
        def: this.authService.getTranslation(environment.defaultLocale),
        t: this.authService.getTranslation(this.profileService.profile.locale)
      }).pipe(
        switchMap(({ contextResponse, def, t }) => {
          // If the saved context is not found or is no longer accessible (no contexts returned), get the first available context
          if (contextResponse.contexts.length === 0) {
            return this.authService.getContextResponse(0, 1).pipe(
              tap(newContextResponse => {
                // If still no contexts are available after fallback, treat the user as unauthorized
                if (newContextResponse.contexts.length === 0) {
                  this.authService.handleUnauthorized();
                  return;
                }

                // Save the fallback context to session storage and update custom claims for next login
                const fallbackContext: Context = newContextResponse.contexts[0];
                this.authService.patchCustomWithLastUsedContext(fallbackContext.contextId, fallbackContext.name)
                  .pipe(first())
                  .subscribe();
                this.authService.setCurrentContextOnStorage(fallbackContext);
              }),
              map(newContextResponse => ({ contextResponse: newContextResponse, def, t })),
              catchError(error => {
                // Fallback request failed - user is unauthorized
                console.error('Failed to get fallback context:', error);
                this.authService.handleUnauthorized();
                return EMPTY;
              })
            );
          }
          return of({ contextResponse, def, t });
          
        }),
        catchError(error => {
          // Main forkJoin failed - user is unauthorized
          console.error('Failed to initialize user context:', error);
          this.authService.handleUnauthorized();
          return EMPTY;
        })
      ).subscribe(({ contextResponse, def, t }) => {
        // Skip initialization if no contexts available (user will be redirected by handleUnauthorized)
        if (contextResponse.contexts.length === 0) {
          return;
        }

        this.profileService.profile.currentContext = contextResponse.contexts[0];
        this.profileService.profile.roles = contextResponse.contexts[0].roles;

        this.i18nService.messages = this.i18nService.extend(def, t);
        this.handleTheme();
        this.handleProfile(hostname);
        this.showWelcomeDialog = false;
        setTimeout(() => {
          this.showWelcomeDialog = this.welcomeDialogDataService.showDialog();
        }, 1000);

        this.onCloseWelcomeDialog();
        const env = this.domainService.getEnviroment(hostname)
        this.dataDogService.init(env, profile.sub, profile.currentContext.businessUnitName);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unlistenVisibility?.();
    this.unlistenCheckSessionMessage?.();
    if (this.checkSessionInterval) {
      clearInterval(this.checkSessionInterval);
    }
    if (this.checkSessionIframe) {
      this.renderer2.removeChild(document.body, this.checkSessionIframe);
      this.checkSessionIframe = null;
    }
    if (this.webSocket) {
      this.webSocket.close();
    }
  }

  onClickNotification(event: Event): void {
    event.preventDefault();
    if (this.notificationItem !== null) {
      this.windowRef.open(this.notificationItem.url, '_blank');
    }
  }

  closeNotification() {
    this.hideNotification();
  }

  private handleProfile(hostname: string): void {
    this.profileService.profile.userDetailsUri = this.authService.getUserDetailsUri(hostname);

    if (this.profileService.profile.roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
      if (this.profileService.profile.roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
        this.handleProfileForBuSubscriberViewer(hostname);
      } else {
        this.handleProfileForRole(hostname);
      }
    } else if (this.profileService.profile.roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
      this.handleProfileForBuSubscriberViewer(hostname);
    } else if (this.profileService.profile.roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
      this.handleProfileForSubscriberAdministrator(hostname);
    } else if (this.profileService.profile.roles.includes(PROFILE_ROLE.administrator)) {
      this.handleProfileForAdministrator(hostname);
    } else if (this.profileService.profile.roles.includes(PROFILE_ROLE.companyAdministrator)) {
      this.configService.hasSystemRoleGroups().pipe(first()).subscribe({
        next: (response) => {
          this.profileService.hasSystemRoleGroups = response;
          this.handleProfileForRole(hostname);
        },
        error: (err) => {
          this.profileService.hasSystemRoleGroups = false;
          this.handleProfileForRole(hostname);
        }
      });
    }
  }

  private handleProfileForSubscriberAdministrator(hostname: string): void {
    forkJoin({
      customer: this.configService.hasAOSetup(),
      hasSystemRoleGroups: this.configService.hasSystemRoleGroups(),
      onPremiseIntegration: this.configService.hasOnPremiseIntegration()
    }).subscribe({
      next: ({ customer, hasSystemRoleGroups, onPremiseIntegration }) => {
        this.handleSubscriberAdministratorProfile(customer.customerIsAO, hasSystemRoleGroups, onPremiseIntegration.opIntegration, hostname);
      },
      error: (err) => {
        this.handleSubscriberAdministratorProfile(false, false, false, hostname);
      }
    })
  }

  private handleProfileForBuSubscriberViewer(hostname: string): void {
    this.configService.hasSubscriberTermination().pipe(first()).subscribe({
      next: (response) => {
        this.handleBuSubscriberViewerProfile(response.subscriberTerminationAllowed, hostname);
      },
      error: (err) => {
        this.handleBuSubscriberViewerProfile(false, hostname);
      }
    });
  }
  
  private handleProfileForAdministrator(hostname: string): void {
    this.profileService.isTestable = this.domainService.isTestable(hostname, this.profileService.profile.email);
    this.hasProfile = true;
    this.router.navigateByUrl(this.authService.getRoutingPathFromRoles(
      this.profileService.profile.roles, this.profileService.profile.currentContext.taskId));
  }

  private handleSubscriberAdministratorProfile(isAOCustomer: boolean, hasSystemRoleGroups: boolean, onPremiseIntegration: boolean, hostname: string): void {
    this.profileService.isAOCustomer = isAOCustomer;
    this.profileService.hasSystemRoleGroups = hasSystemRoleGroups;
    this.profileService.onPremiseIntegration = onPremiseIntegration;
    this.profileService.isTestable = this.domainService.isTestable(hostname, this.profileService.profile.email);
    this.hasProfile = true;
    this.initWebSocket();
    this.router.navigateByUrl(this.authService.getRoutingPathFromRoles(this.profileService.profile.roles));
  }

  private handleBuSubscriberViewerProfile(subscriberTerminationAllowed: boolean, hostname: string): void {
    this.profileService.allowSubscriberTermination = subscriberTerminationAllowed;
    this.profileService.isTestable = this.domainService.isTestable(hostname, this.profileService.profile.email);
    this.hasProfile = true;
    this.initWebSocket();
    this.onRouteChange();
    this.router.navigateByUrl(this.authService.getRoutingPathFromRoles(this.profileService.profile.roles));
  }

  private handleProfileForRole(hostname: string): void {
    this.profileService.isTestable = this.domainService.isTestable(hostname, this.profileService.profile.email);
    this.hasProfile = true;
    this.initWebSocket();
    this.onRouteChange();
    this.router.navigateByUrl(this.authService.getRoutingPathFromRoles(this.profileService.profile.roles));
  }

  private onRouteChange(): void {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        if (event.url !== '' && event.url !== '/') {
          this.entityOptionsMenuDataService.resetUIParentItem();
          this.serviceAccessDataService.resetSelectedIdsForMain();
        }
      }
    });
  }

  private handleTheme(): void {
    this.profileService.profile.theme = THEME.light;
    this.profileService.setTheme(THEME.light);
  }

  private handleI18N(): void {
    const locale = this.i18nService.getLocale(environment.supportedLocale,
      environment.defaultLocale, this.profileService.profile.locale);
    this.profileService.profile.locale = locale;
  }

  private handleLastUsedContextIdDifferentThanTenantIdFromToken(profile: Profile, hostname: string): void {
    const lastUsedContextId = profile.custom[LAST_USED_TENANT_ID];
    if (lastUsedContextId) {
      const str = sessionStorage.getItem(TOKEN_RESPONSE);
      if (str) {
        const tenantId = this.authService.getTenantId(hostname);
        if (tenantId && tenantId !== lastUsedContextId) {
          this.authService.setCurrentContextOnStorage({ contextId: lastUsedContextId, name: profile.custom[LAST_USED_TENANT_NAME] } as Context);
          const url = this.authService.getConnectLoginUrl(hostname);
          this.windowRef.setLocationHref(url);
        }
      }
    }
  }

  private getStoredContext(): Context | null {
    const str = sessionStorage.getItem(ADMIN_UI_CURRENT_CONTEXT);
    return str ? JSON.parse(str) : null;
  }

  private initCheckSession(hostname: string): void {
    const config = this.authService.getCheckSessionConfig(hostname);
    if (!config) {
      return;
    }

    const { iframeUrl, clientId, sessionState } = config;

    let iframeOrigin: string;
    try {
      iframeOrigin = new URL(iframeUrl).origin;
    } catch (error) {
      console.error('Invalid check_session_iframe URL:', iframeUrl, error);
      return;
    }
    const iframe = this.renderer2.createElement('iframe') as HTMLIFrameElement;
    this.renderer2.setStyle(iframe, 'display', 'none');
    this.renderer2.setAttribute(iframe, 'src', iframeUrl);
    this.renderer2.appendChild(document.body, iframe);
    this.checkSessionIframe = iframe;

    this.unlistenCheckSessionMessage = this.renderer2.listen('window', 'message', (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) {
        return;
      }
      if (event.source !== this.checkSessionIframe?.contentWindow) {
        return;
      }
      if (event.data === 'unchanged') {
        this.checkSessionEstablished = true;
      } else if (event.data === 'changed' && this.checkSessionEstablished) {
        const logoutUrl = this.authService.getConnectLogoutUrl(hostname);
        if (logoutUrl) {
          this.windowRef.setLocationHref(logoutUrl);
        }
      }
    });

    this.checkSessionInterval = setInterval(() => {
      if (this.checkSessionIframe?.contentWindow) {
        this.checkSessionIframe.contentWindow.postMessage(`${clientId} ${sessionState}`, iframeOrigin);
      }
    }, 10 * 1000);
  }

  private initWebSocket(): void {
    const hostname = this.windowRef.getHostname();

    if (hostname !== HOSTNAME.int) {
      const access_token = this.authService.getAccessToken();
      if (access_token !== null) {
        const msg = this.i18nService.messages['notifications'];

        this.webSocket = new WebSocket(this.getConnectionUrl(hostname, access_token));
        this.webSocket.onopen = () => {
          console.log('WebSocket on open');
          this.pingInterval = setInterval(() => {
            console.log("WebSocket send ping");
            const sendMessage = JSON.stringify({ action: 'ping' });
            this.webSocket.send(sendMessage);
          }, 5 * 60000);
        };

        this.webSocket.onmessage = (event: any) => {
          this.alertFlashDataService.reset();
          this.alertErrorDataService.reset();
          this.notificationItem = JSON.parse(event.data);

          if (this.notificationItem && this.notificationItem.entityName) {
            if (NOTIFICATION_EXPORT_KEYS.includes(this.notificationItem.notificationType)) {
              this.notificationItem.content = this.i18nService.getFormattedMessage(msg['infoYourExportIsReady'],
                [msg[this.notificationItem.notificationType], this.notificationItem.entityName]);

              this.notificationItem.createdDateFormatted = this.i18nService.formatDateTimeDefault(Number(this.notificationItem.createdDate));
              this.notificationItem.deleteAfterFormatted = this.i18nService.formatDateTimeDefault(Number(this.notificationItem.deleteAfter) * 1000);
              this.latestExportsService.addExport(this.notificationItem);


            } else if (NOTIFICATION_COPY_USER_ACCESS === this.notificationItem.notificationType) {
              this.serviceAccessDataService.removeFromPendingIdsAndRefreshTheUserList(this.notificationItem.entityId);

            } else if (NOTIFICATION_INCLUDE_ALL_MEMBERS.includes(this.notificationItem.notificationType)) {
              let key;
              if (this.notificationItem.notificationType === NOTIFICATION_INCLUDE_ALL_MEMBERS[0]) {
                key = this.entityDetailsDataService.getIncludeAllMembersSessionStorageKey(this.notificationItem.entityType,
                  this.notificationItem.entityId);
              } else if (this.notificationItem.notificationType === NOTIFICATION_INCLUDE_ALL_MEMBERS[1]) {
                key = this.entityDetailsDataService.getIncludeAllMembersSessionStorageKey(this.notificationItem.entityType,
                  this.notificationItem.entityId, ENTITY_TYPE.company);
              } else {
                key = this.entityDetailsDataService.getIncludeAllMembersSessionStorageKey(this.notificationItem.entityType,
                  this.notificationItem.entityId, ENTITY_TYPE.clientCompany);
              }
              sessionStorage.removeItem(key);
              this.entityDetailsDataService.removeReadOnly();

              this.notificationTitle = this.i18nService.getFormattedMessage(msg[this.notificationItem.notificationType],
                [this.notificationItem.entityName]);

              this.displayNotification();

            } else {
              this.notificationTitle = this.i18nService.getFormattedMessage(msg['infoYourImportIsReady'],
                [this.notificationItem.entityName]);
              this.displayNotification();
            }
          }
        };

        this.webSocket.onerror = (event: any) => {
          console.error("WebSocket error received: ", event);
          clearInterval(this.pingInterval);
        }

        this.webSocket.onclose = (event: any) => {
          console.log("WebSocket closed: ", event);
          clearInterval(this.pingInterval);
        }
      }
    }
  }

  private displayNotification(): void {
    this.showNotification = true;
    this.notificationTimeout = setTimeout(() => {
      this.hideNotification();
    }, 30000);
  }

  private hideNotification(): void {
    this.showNotification = false;
    this.notificationItem = null;
    clearTimeout(this.notificationTimeout);
  }

  private getConnectionUrl(hostname: string, accessToken: string): string {
    const env = this.domainService.getEnviroment(hostname);
    const nhub = <{ [key: string]: NotificationsHub }>environment.notificationsHub;
    return `${nhub[env].url}?access-token=${accessToken}`;
  }

  private onCloseWelcomeDialog(): void {
    this.welcomeDialogDataService.closeDialog$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.showWelcomeDialog = false;
        }
      });
  }

}


